import { motion } from "framer-motion";
import React from "react";
import { FindMoreTimesButton } from "./FindMoreTimesButton";
import { Message } from "./QuickRescheduleMessage";
import { QuickRescheduleOption } from "./QuickRescheduleOption";
import { getLabelTextFromISO } from "./util/getLabelTextFromISO";

export type RescheduleState = "default" | "selected" | "confirming" | "confirmed";

type Props = {
  canProposeNewTime: boolean;
  confirmationMessage?: string;
  emphasis?: boolean;
  errorMessage?: string;
  loading?: boolean;
  onConfirm: () => void;
  onFindMoreTimes: () => void;
  onHover?: (startISO: string | null) => void;
  onSelect: (selectedISO: string | null) => void;
  options?: string[];
  state: RescheduleState;
  value?: string | null;
};

export const QuickReschedule = ({
  canProposeNewTime,
  confirmationMessage = "",
  emphasis = false,
  errorMessage = "",
  loading = false,
  onConfirm,
  onFindMoreTimes,
  onHover,
  onSelect,
  options = [],
  state,
  value = null,
}: Props) => {
  const renderOptionsOrProposeText = () => {
    if (canProposeNewTime) {
      return (
        <div style={{ textWrap: "wrap" }} className="cw-space-y-1">
          You don't have permission to reschedule this event, but you can propose a new time.
        </div>
      );
    } else {
      return (
        <motion.div
          initial={{ display: "flex" }}
          animate={
            state === "confirmed"
              ? {
                  height: 0,
                  transition: { type: "tween", duration: 0.25 },
                  transitionEnd: { display: "none" },
                }
              : {}
          }
          style={{ overflow: "hidden" }}
          className="cw-flex-col cw-space-y-2"
        >
          {options.map((option, index) => {
            const toggleSelected = () => onSelect(option === value ? null : option);
            const handleHover = (hovered: boolean): void => onHover?.(hovered ? option : null);
            const text = getLabelTextFromISO(option);

            return (
              <QuickRescheduleOption
                emphisis={emphasis}
                isSelected={option === value}
                key={index}
                onConfirm={onConfirm}
                onHover={handleHover}
                onSelect={toggleSelected}
                state={state}
                text={text}
              />
            );
          })}
          {options.length === 0 && (
            <Message sentiment="default">No available times within 2 weekdays</Message>
          )}
        </motion.div>
      );
    }
  };

  return (
    <div className="cw-body-base">
      <div className="cw-flex cw-flex-col cw-space-y-2">
        {errorMessage && <Message sentiment="warning">{errorMessage}</Message>}
        {confirmationMessage && <Message sentiment="info">{confirmationMessage}</Message>}
        {loading && !canProposeNewTime ? (
          <>
            <QuickRescheduleOption state="default" loading disabled />
            <QuickRescheduleOption state="default" loading disabled />
            <QuickRescheduleOption state="default" loading disabled />
          </>
        ) : (
          renderOptionsOrProposeText()
        )}
        <FindMoreTimesButton
          canProposeNewTime={canProposeNewTime}
          disabled={state === "confirming"}
          emphisis={emphasis}
          onClick={onFindMoreTimes}
        />
      </div>
    </div>
  );
};
